import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stethoscope,
  Scissors,
  Syringe,
  Bug,
  Footprints,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { useAppointments } from '../../../contexts/AppointmentsContext';
import { usePets } from '../../../contexts/PetsContext';
import { useToast } from '../../../contexts/ToastContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Card from '../../../components/ui/Card';
import {
  APPOINTMENT_TYPES,
  REMINDER_MINUTES_OPTIONS,
  ROUTES,
  VALIDATION,
} from '../../../config/constants';
import { formatList } from '../../../lib/format';
import PetMultiSelector from './PetMultiSelector';

/**
 * Form de creación / edición de cita.
 *
 * Props:
 *  - appointment?: si está, modo edit (no se editan petIds, se muestran read-only).
 *                  si no, modo create.
 *
 * Mapping de error.code → campo del form para inline display.
 */

const TYPE_OPTIONS = [
  { value: APPOINTMENT_TYPES.VET,       icon: Stethoscope, label: 'Vet' },
  { value: APPOINTMENT_TYPES.GROOMING,  icon: Scissors,    label: 'Peluquería' },
  { value: APPOINTMENT_TYPES.VACCINE,   icon: Syringe,     label: 'Vacuna' },
  { value: APPOINTMENT_TYPES.DEWORMING, icon: Bug,         label: 'Desparasitación' },
  { value: APPOINTMENT_TYPES.WALK,      icon: Footprints,  label: 'Paseo' },
  { value: APPOINTMENT_TYPES.OTHER,     icon: CalendarDays,label: 'Otro' },
];

const REMINDER_LABELS = {
  0: 'Sin recordatorio',
  15: '15 min antes',
  30: '30 min antes',
  60: '1 h antes',
  120: '2 h antes',
  1440: '1 día antes',
};

const FIELD_ERROR_MAP = {
  INVALID_TITLE: 'title',
  INVALID_NOTES: 'notes',
  INVALID_TYPE: 'type',
  INVALID_SCHEDULED_AT: 'scheduledAt',
  INVALID_REMINDER: 'reminderMinutes',
  INVALID_PET_IDS: 'petIds',
  CROSS_TENANT_PET: 'petIds',
};

/** Convierte un Date a "YYYY-MM-DDTHH:mm" en zona local (para datetime-local). */
const dateToInputValue = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const timestampToDate = (ts) => {
  if (!ts) return null;
  if (typeof ts.toMillis === 'function') return new Date(ts.toMillis());
  if (ts instanceof Date) return ts;
  return null;
};

const AppointmentForm = ({ appointment = null }) => {
  const isEdit = Boolean(appointment);
  const {
    createAppointment,
    updateAppointment,
    completeAppointment,
    cancelAppointment,
    softDeleteAppointment,
  } = useAppointments();
  const { pets } = usePets();
  const toast = useToast();
  const navigate = useNavigate();

  // Default scheduled: dentro de 1h, redondeado a los próximos 15 minutos.
  const defaultScheduled = useMemo(() => {
    if (isEdit) return timestampToDate(appointment.scheduledAt);
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
    return d;
  }, [isEdit, appointment]);

  const [type, setType] = useState(appointment?.type || APPOINTMENT_TYPES.VET);
  const [title, setTitle] = useState(appointment?.title || '');
  const [notes, setNotes] = useState(appointment?.notes || '');
  const [scheduledAtInput, setScheduledAtInput] = useState(dateToInputValue(defaultScheduled));
  const [reminderMinutes, setReminderMinutes] = useState(
    appointment?.reminderMinutes ?? 60
  );
  const [selectedPetIds, setSelectedPetIds] = useState(
    appointment?.petIds ? [...appointment.petIds] : []
  );

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [actionInFlight, setActionInFlight] = useState(null);

  const isCompleted = isEdit && Boolean(appointment.completedAt);
  const isCancelled = isEdit && Boolean(appointment.cancelledAt);
  const isFinalized = isCompleted || isCancelled;

  // Aviso si reminderMinutes es mayor que el tiempo restante (no es bloqueante).
  const reminderWarning = useMemo(() => {
    if (reminderMinutes === 0) return null;
    if (!scheduledAtInput) return null;
    const targetMs = new Date(scheduledAtInput).getTime();
    if (Number.isNaN(targetMs)) return null;
    const diffMin = (targetMs - Date.now()) / 60_000;
    if (diffMin < reminderMinutes && diffMin > 0) {
      return 'El recordatorio cae en menos tiempo del que falta para la cita — puede que no llegue antes.';
    }
    return null;
  }, [reminderMinutes, scheduledAtInput]);

  const handleErrorFromService = (err) => {
    const field = FIELD_ERROR_MAP[err.code];
    if (field) {
      setErrors({ [field]: err.message });
    } else {
      toast.error(err.message || 'No se pudo guardar la cita.');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrors({});

    if (!isEdit && selectedPetIds.length === 0) {
      setErrors({ petIds: 'Elegí al menos una mascota' });
      return;
    }
    if (!title.trim()) {
      setErrors({ title: 'El título es requerido' });
      return;
    }
    if (!scheduledAtInput) {
      setErrors({ scheduledAt: 'Elegí la fecha y hora' });
      return;
    }
    const scheduledDate = new Date(scheduledAtInput);
    if (Number.isNaN(scheduledDate.getTime())) {
      setErrors({ scheduledAt: 'Fecha inválida' });
      return;
    }

    try {
      setSubmitting(true);
      if (isEdit) {
        await updateAppointment(appointment.id, {
          type,
          title,
          notes: notes.trim() || null,
          scheduledAt: scheduledDate,
          reminderMinutes,
        });
        toast.success('Cita actualizada.');
        navigate(ROUTES.CALENDAR);
      } else {
        await createAppointment({
          petIds: selectedPetIds,
          type,
          title,
          notes: notes.trim() || null,
          scheduledAt: scheduledDate,
          reminderMinutes,
        });
        toast.success('Cita agendada.');
        navigate(ROUTES.CALENDAR);
      }
    } catch (err) {
      handleErrorFromService(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async () => {
    if (!appointment) return;
    try {
      setActionInFlight('complete');
      await completeAppointment(appointment.id);
      toast.success('Cita marcada como hecha.');
      navigate(ROUTES.CALENDAR);
    } catch (err) {
      toast.error(err.message || 'No se pudo marcar como hecha.');
    } finally {
      setActionInFlight(null);
    }
  };

  const handleCancel = async () => {
    if (!appointment) return;
    if (!window.confirm('¿Cancelar esta cita? Queda registrada en la bitácora.')) return;
    try {
      setActionInFlight('cancel');
      await cancelAppointment(appointment.id);
      toast.info('Cita cancelada.');
      navigate(ROUTES.CALENDAR);
    } catch (err) {
      toast.error(err.message || 'No se pudo cancelar.');
    } finally {
      setActionInFlight(null);
    }
  };

  const handleDelete = async () => {
    if (!appointment) return;
    if (!window.confirm('¿Eliminar esta cita? No se va a poder recuperar.')) return;
    try {
      setActionInFlight('delete');
      await softDeleteAppointment(appointment.id);
      toast.info('Cita eliminada.');
      navigate(ROUTES.CALENDAR);
    } catch (err) {
      toast.error(err.message || 'No se pudo eliminar.');
    } finally {
      setActionInFlight(null);
    }
  };

  // En edit mode, resolver los nombres de las pets para mostrar read-only.
  const editPetNames = useMemo(() => {
    if (!isEdit) return [];
    return (appointment.petIds || [])
      .map((id) => pets.find((p) => p.id === id)?.name ?? null)
      .filter(Boolean);
  }, [isEdit, appointment, pets]);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Tipo */}
      <Card>
        <h2 className="font-semibold text-slate-900">Tipo de cita</h2>
        <div className="mt-3 grid grid-cols-3 gap-2">
          {TYPE_OPTIONS.map(({ value, icon: Icon, label }) => {
            const active = type === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setType(value)}
                disabled={submitting || isFinalized}
                className={[
                  'flex flex-col items-center gap-1 rounded-xl border px-2 py-2.5 text-xs font-medium transition-colors',
                  active
                    ? 'border-brand-500 bg-brand-50 text-brand-800'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                  (submitting || isFinalized) ? 'cursor-not-allowed opacity-60' : '',
                ].join(' ')}
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
                {label}
              </button>
            );
          })}
        </div>
        {errors.type && (
          <p role="alert" className="mt-2 text-sm text-status-red">{errors.type}</p>
        )}
      </Card>

      {/* Título + Notas */}
      <Card>
        <Input
          label="Título"
          placeholder="Ej: Vacuna anual de Luna"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={errors.title}
          maxLength={VALIDATION.APPOINTMENT_TITLE_MAX}
          autoFocus={!isEdit}
          disabled={submitting || isFinalized}
        />
        <div className="mt-3">
          <label className="mb-1.5 block text-sm font-medium text-slate-700">
            Notas (opcional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={VALIDATION.APPOINTMENT_NOTES_MAX}
            disabled={submitting || isFinalized}
            placeholder="Recordatorios, indicaciones, qué llevar..."
            rows={3}
            className="block w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:bg-slate-100 disabled:text-slate-500"
          />
          {errors.notes && (
            <p role="alert" className="mt-1.5 text-sm text-status-red">{errors.notes}</p>
          )}
        </div>
      </Card>

      {/* Fecha + Recordatorio */}
      <Card>
        <Input
          label="Fecha y hora"
          type="datetime-local"
          value={scheduledAtInput}
          onChange={(e) => setScheduledAtInput(e.target.value)}
          error={errors.scheduledAt}
          disabled={submitting || isFinalized}
        />

        <div className="mt-4">
          <label className="mb-1.5 block text-sm font-medium text-slate-700">
            Recordatorio
          </label>
          <div className="flex flex-wrap gap-1.5">
            {REMINDER_MINUTES_OPTIONS.map((mins) => {
              const active = reminderMinutes === mins;
              return (
                <button
                  key={mins}
                  type="button"
                  onClick={() => setReminderMinutes(mins)}
                  disabled={submitting || isFinalized}
                  className={[
                    'rounded-xl border px-3 py-1.5 text-sm font-medium transition-colors',
                    active
                      ? 'border-brand-500 bg-brand-50 text-brand-800'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                    (submitting || isFinalized) ? 'cursor-not-allowed opacity-60' : '',
                  ].join(' ')}
                >
                  {REMINDER_LABELS[mins]}
                </button>
              );
            })}
          </div>
          {errors.reminderMinutes && (
            <p role="alert" className="mt-1.5 text-sm text-status-red">{errors.reminderMinutes}</p>
          )}
          {reminderWarning && (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              {reminderWarning}
            </p>
          )}
        </div>
      </Card>

      {/* Mascotas */}
      <Card>
        <h2 className="font-semibold text-slate-900">Mascotas</h2>
        <p className="mt-0.5 text-sm text-slate-500">
          {isEdit
            ? 'No se pueden cambiar después de creada la cita.'
            : 'Elegí una o varias mascotas afectadas.'}
        </p>
        <div className="mt-3">
          {isEdit ? (
            <p className="rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
              {editPetNames.length > 0
                ? `Para ${formatList(editPetNames)}`
                : 'Mascotas no disponibles (pueden haber sido eliminadas)'}
            </p>
          ) : (
            <PetMultiSelector
              pets={pets}
              selectedIds={selectedPetIds}
              onChange={setSelectedPetIds}
              error={errors.petIds}
              disabled={submitting}
            />
          )}
        </div>
      </Card>

      {/* Submit + acciones de edit */}
      {!isFinalized && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            fullWidth
            onClick={() => navigate(ROUTES.CALENDAR)}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            fullWidth
            isLoading={submitting}
          >
            {isEdit ? 'Guardar cambios' : 'Agendar'}
          </Button>
        </div>
      )}

      {isEdit && !isFinalized && (
        <Card>
          <h2 className="font-semibold text-slate-900">Acciones</h2>
          <div className="mt-3 flex flex-col gap-2">
            <Button
              type="button"
              variant="primary"
              leftIcon={CheckCircle2}
              onClick={handleComplete}
              isLoading={actionInFlight === 'complete'}
              disabled={Boolean(actionInFlight)}
            >
              Marcar como hecha
            </Button>
            <Button
              type="button"
              variant="secondary"
              leftIcon={XCircle}
              onClick={handleCancel}
              isLoading={actionInFlight === 'cancel'}
              disabled={Boolean(actionInFlight)}
            >
              Cancelar cita
            </Button>
            <Button
              type="button"
              variant="danger"
              leftIcon={Trash2}
              onClick={handleDelete}
              isLoading={actionInFlight === 'delete'}
              disabled={Boolean(actionInFlight)}
            >
              Eliminar
            </Button>
          </div>
        </Card>
      )}

      {isEdit && isFinalized && (
        <Card>
          <p className="text-sm text-slate-600">
            {isCompleted
              ? 'Esta cita ya fue marcada como hecha. Quedó en la bitácora.'
              : 'Esta cita fue cancelada. Quedó en la bitácora.'}
          </p>
          <div className="mt-3">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => navigate(ROUTES.CALENDAR)}
            >
              Volver al calendario
            </Button>
          </div>
        </Card>
      )}
    </form>
  );
};

export default AppointmentForm;
